import React from 'react';

// What the schema editors check before a schema is made or changed: whether one
// by that name already exists (to reuse rather than invent), and whether a new
// version changes what is encrypted (a migration event, acknowledged first).
// Its own module so the editors share it and a test may mock the builder alone.

// A name as a person means it: no prefix, no case, no separators — so
// "Medical Record", "medical_record" and "custom:medical-record" are one name.
const bareKey = (name) => String(name || '').toLowerCase().replace(/^[^:]*:/, '').replace(/[^a-z0-9]/g, '');

/** SEARCH BEFORE CREATE: the schemas that already answer to a name being typed,
 *  one per name (its newest version), the exact match first. A schema invented
 *  instead of reused is invisible to every other app, permanently. */
export function similarSchemas(name, all = [], limit = 5) {
  const q = bareKey(name);
  if (q.length < 2) return [];
  const newest = new Map();
  for (const s of all) {
    if (!s?.schema_name || !bareKey(s.schema_name).includes(q)) continue;
    const had = newest.get(s.schema_name);
    if (!had || (s.version || 0) > (had.version || 0)) newest.set(s.schema_name, s);
  }
  return [...newest.values()]
    .sort((a, b) => (bareKey(a.schema_name) === q ? -1 : 0) - (bareKey(b.schema_name) === q ? -1 : 0)
      || bareKey(a.schema_name).length - bareKey(b.schema_name).length)
    .slice(0, limit);
}

/** Which fields a change starts and stops encrypting. */
export function encryptionChange(before = [], after = []) {
  const was = new Set(before);
  const now = new Set(after);
  return { added: [...now].filter((k) => !was.has(k)), removed: [...was].filter((k) => !now.has(k)) };
}

/** THE ACKNOWLEDGEMENT an encryption change needs: nodes already written are not
 *  migrated, so the corpus is mixed until they are. Nothing saves until it is ticked. */
export function EncryptionAck({ change, checked, onChange }) {
  if (!change.added.length && !change.removed.length) return null;
  return (
    <div className="sbuilder__compat-warning" role="group" aria-label="Encryption change">
      <div className="sbuilder__compat-title">This changes which fields are encrypted</div>
      <ul className="sbuilder__compat-list">
        {change.added.length > 0 && <li>Encrypting {change.added.join(', ')}: nodes already written keep these fields as readable plaintext until they are rewritten.</li>}
        {change.removed.length > 0 && <li>No longer encrypting {change.removed.join(', ')}: nodes already written keep these fields as ciphertext only each node’s owner can open.</li>}
      </ul>
      <label className="sbuilder__toggle-label">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span>I understand nodes already written are not changed</span>
      </label>
    </div>
  );
}
